import { useEffect, useState } from 'react';
import { useApiClient } from '../../app/ApiClientContext';
import { useToast } from '../../app/ToastProvider';
import { BrandLogo } from '../../components/BrandLogo';
import { Button } from '../../components/Button';
import { Spinner } from '../../components/Spinner';

type Phase =
  | { kind: 'pending' }                                       // requesting /oidc/redirect
  | { kind: 'redirecting'; authorizationUrl: string }         // got URL, location.assign issued
  | { kind: 'error'; message: string }
  | { kind: 'manual' };                                       // ?logged_out=1 — show button instead

function readLoggedOutFlag(): boolean {
  return new URLSearchParams(window.location.search).get('logged_out') === '1';
}

export function LoginScreen() {
  const client = useApiClient();
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>(() =>
    readLoggedOutFlag() ? { kind: 'manual' } : { kind: 'pending' },
  );

  async function startLogin() {
    setPhase({ kind: 'pending' });
    const r = await client.GET('/api/v2/oidc/redirect');
    if (!r.data) {
      setPhase({ kind: 'error', message: 'Не удалось начать вход. Попробуйте ещё раз.' });
      toast.push('error', 'Сервер недоступен');
      return;
    }
    const url = r.data.authorization_url;
    setPhase({ kind: 'redirecting', authorizationUrl: url });
    window.location.assign(url);
  }

  useEffect(() => {
    if (phase.kind === 'pending') {
      // Clear the ?logged_out=1 param if present *before* auto-redirecting next time.
      // Inside 'pending' branch we know we are NOT in the manual flow.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional bootstrap kick-off on mount
      void startLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function manualRetry() {
    const url = new URL(window.location.href);
    url.searchParams.delete('logged_out');
    window.history.replaceState({}, '', url.toString());
    void startLogin();
  }

  return (
    <main className="min-h-screen grid place-items-center px-6">
      <div className="max-w-sm w-full text-center space-y-6">
        <div className="flex justify-center">
          <BrandLogo />
        </div>
        <p className="text-sm text-[var(--color-text-muted)]">Короткие ссылки</p>

        {phase.kind === 'pending' && (
          <div className="flex items-center justify-center gap-2 text-sm">
            <Spinner /> Перенаправляем…
          </div>
        )}

        {phase.kind === 'redirecting' && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Spinner /> Перенаправляем…
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              Не сработал автоматический вход?{' '}
              <a
                href={phase.authorizationUrl}
                className="underline text-[var(--color-blue)]"
              >
                Перейти к входу
              </a>
            </p>
          </div>
        )}

        {phase.kind === 'manual' && (
          <div className="space-y-3">
            <p className="text-sm">Вы вышли из аккаунта.</p>
            <Button onClick={manualRetry}>Войти заново</Button>
          </div>
        )}

        {phase.kind === 'error' && (
          <div className="space-y-3">
            <p className="text-sm">{phase.message}</p>
            <Button onClick={startLogin}>Повторить</Button>
          </div>
        )}
      </div>
    </main>
  );
}
